import React, { useState } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import { Calculate, Info } from '@mui/icons-material';
import { MathFunctions } from '../utils/MathFunctions';
import ResponsiveDiagram from './ResponsiveDiagram';

const RadCalc: React.FC = () => {
  const [length, setLength] = useState('');
  const [height, setHeight] = useState('');
  const [radius, setRadius] = useState<string | null>(null);
  const [radiusLength, setRadiusLength] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [calculatedInputs, setCalculatedInputs] = useState<string | null>(null);
  const currentInputs = JSON.stringify([length, height]);
  const isStale = calculatedInputs !== null && calculatedInputs !== currentInputs;

  const handleCalculate = () => {
    if (!length || !height) {
      setShowError(true);
      return;
    }

    const lengthNum = parseFloat(length);
    const heightNum = parseFloat(height);

    if (isNaN(lengthNum) || isNaN(heightNum)) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setCalculatedInputs(currentInputs);

    const calculatedRadius = MathFunctions.calcRad(lengthNum, heightNum);
    const angle = MathFunctions.calcAngle(lengthNum, calculatedRadius);
    const calculatedRadLength = MathFunctions.calcRadLength(calculatedRadius, angle);

    setRadius(calculatedRadius.toString());
    setRadiusLength(calculatedRadLength.toString());
  };

  const hasResults = radius && radiusLength;

  return (
    <Box component="main" id="main-content" tabIndex={-1} sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F5F5F5 0%, #E8EAF6 100%)',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Calculate Radius
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Find radius and arc length from chord measurements
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Input Form */}
          <Card component="form" noValidate onSubmit={(event) => { event.preventDefault(); handleCalculate(); }} elevation={3} sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Calculate sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Input Values
                </Typography>
              </Box>

              <Stack spacing={3}>
                {showError && (
                  <Alert severity="error">Enter a number in each highlighted field, then calculate again.</Alert>
                )}
                <TextField
                  fullWidth
                  label="Length"
                  type="number"
                  value={length}
                  error={showError && (!length || isNaN(parseFloat(length)))}
                  onChange={(e) => setLength(e.target.value)}
                  inputProps={{ 
                    step: "0.01",
                    inputMode: 'decimal'
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Length of the chord across the radius"
                />
                
                <TextField
                  fullWidth
                  label="Height"
                  type="number"
                  value={height}
                  error={showError && (!height || isNaN(parseFloat(height)))}
                  onChange={(e) => setHeight(e.target.value)}
                  inputProps={{ 
                    step: "0.01",
                    inputMode: 'decimal'
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Height from center of chord to radius"
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  startIcon={<Calculate />}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #1976D2 30%, #42A5F5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #0D47A1 30%, #1976D2 90%)',
                    }
                  }}
                >
                  Calculate
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Results */}
          <Card component="section" aria-label="Calculation results" aria-live="polite" aria-atomic="true" elevation={3} sx={{ flex: 1 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Results
                </Typography>
              </Box>

              {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Inputs changed. These results are from your previous calculation. Calculate again to update them.
                </Alert>
              )}

              {hasResults ? (
                <Stack spacing={2}>
                  <Box>
                    <Chip 
                      label="Radius" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Radius" }}
                      fullWidth
                      value={`${radius} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box>
                    <Chip 
                      label="Arc Length" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Arc Length" }}
                      fullWidth
                      value={`${radiusLength} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mt: 2, p: 2, bgcolor: '#edf5fc', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.primary">
                      <strong>Formula:</strong> r = (4h² + l²) / 8h
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <Calculate sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">
                    Enter values and click Calculate to see results
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Diagram */}
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              Reference Diagram
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveDiagram 
              imageName="rad" 
              alt="Radius calculation diagram showing chord length, height, and radius relationship" 
            />
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
};

export default RadCalc;